/**
 * LA REGOLA DELL'ULTIMO SALVAGENTE — una sola, copiata pari pari dal server
 * (supabase/functions/_shared/salvagente.ts). Il client la usa per DECIDERE
 * SE MOSTRARE il tasto; il server (richiedi-operatore) la riapplica prima di
 * aprire la porta. Se cambia di la', cambia anche qui, lo stesso giorno.
 *
 * Decisa il 23-24/09/2026 (Umberto, Davide):
 * - il passaggio ai tecnici compare dopo N risposte che non risolvono nella
 *   STESSA conversazione (N = soglia dal pannello, si parte da 5);
 * - «ti ho aiutato» azzera il conto: si contano solo le risposte dopo
 *   l'ultimo aiutato;
 * - chi chiede esplicitamente un operatore per la seconda volta non deve
 *   aspettare la quinta risposta: accorcia la strada, non la salta.
 */

export const CHIAVE_SOGLIA = "soglia_banco";
export const SOGLIA_PREDEFINITA = 5;
export const SOGLIA_MINIMA = 2;
export const SOGLIA_MASSIMA = 20;

/** «voglio parlare con un operatore», «chiamatemi», «assistenza umana»… */
export const RICHIESTA_ESPLICITA =
  /\b(operat(?:ore|rice|ori)|assistenza (?:umana|telefonica)|parlare con (?:qualcuno|una persona|un tecnico|un umano|un operatore|voi)|una persona (?:vera|reale)|un tecnico (?:vero|di datacom|datacom)|chiamatemi|richiamatemi|voglio (?:essere )?(?:chiamat|richiamat)[oa]|passami (?:un|il) (?:tecnico|operatore))\b/i;

export function eRichiestaEsplicita(testo: string): boolean {
  return RICHIESTA_ESPLICITA.test(testo ?? "");
}

export interface MessaggioContato {
  role: string;
  content: string;
  /** millisecondi dall'epoca */
  quando: number;
}

export interface GiudizioContato {
  esito: string;
  quando: number;
}

export interface ContoConversazione {
  /** risposte dell'assistente dopo l'ultimo «ti ho aiutato» */
  risposte: number;
  nonAiutato: number;
  /** domande che chiedono esplicitamente una persona, dopo l'ultimo «aiutato» */
  esplicite: number;
  /** c'e' stato almeno un «ti ho aiutato» in tutta la conversazione */
  aiutatoMai: boolean;
}

export function contaConversazione(messaggi: MessaggioContato[], giudizi: GiudizioContato[]): ContoConversazione {
  let ultimoAiutato = 0;
  let aiutatoMai = false;
  for (const g of giudizi) {
    if (g.esito === "aiutato") {
      aiutatoMai = true;
      if (g.quando > ultimoAiutato) ultimoAiutato = g.quando;
    }
  }
  let risposte = 0;
  let esplicite = 0;
  for (const m of messaggi) {
    if (m.quando <= ultimoAiutato) continue;
    if (m.role === "assistant") risposte += 1;
    else if (m.role === "user" && eRichiestaEsplicita(m.content)) esplicite += 1;
  }
  const nonAiutato = giudizi.filter((g) => g.esito === "non_aiutato" && g.quando > ultimoAiutato).length;
  return { risposte, nonAiutato, esplicite, aiutatoMai };
}

export type EsitoSalvagente = "no" | "soglia" | "esplicita";

export function serveSalvagente(c: ContoConversazione, soglia: number = SOGLIA_PREDEFINITA): EsitoSalvagente {
  if (c.esplicite >= 2) return "esplicita";
  if (c.risposte >= soglia) return "soglia";
  return "no";
}

/** Il valore jsonb di `impostazioni.soglia_banco`, reso un numero sensato. */
export function leggiSoglia(valore: unknown): number {
  const n = typeof valore === "number" ? valore : Number(valore);
  if (!Number.isFinite(n)) return SOGLIA_PREDEFINITA;
  return Math.min(SOGLIA_MASSIMA, Math.max(SOGLIA_MINIMA, Math.round(n)));
}
